import { useState, useEffect, useMemo } from "react";
import { SimpleDialog } from "@/components/ui/simple-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PRESET_BY_SOURCE } from "@/data/presets";
import { boardKeyFromName } from "@/components/BoardTracker";

function minutesUntil(iso) {
  if (!iso) return 30;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(1, Math.round(ms / 60000));
}

const accent = (mode) =>
  mode === "private"
    ? {
        border: "border-emerald-500/30",
        headerBorder: "border-emerald-500/15",
        iconBorder: "border-emerald-500/40",
        iconBg: "bg-emerald-500/5",
        icon: "text-emerald-400",
        label: "text-emerald-400",
        focusBorder: "focus:border-emerald-500",
        focusRing: "focus-visible:ring-emerald-500",
        submitBtn: "border-emerald-500 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-black",
      }
    : {
        border: "border-orange-500/30",
        headerBorder: "border-orange-500/15",
        iconBorder: "border-orange-500/40",
        iconBg: "bg-orange-500/5",
        icon: "text-orange-400",
        label: "text-orange-400",
        focusBorder: "focus:border-orange-500",
        focusRing: "focus-visible:ring-orange-500",
        submitBtn: "border-orange-500 bg-orange-500/10 text-orange-400 hover:bg-orange-500 hover:text-black",
      };

export default function CardDialog({ open, onOpenChange, initial, onSubmit, onClaim, error, onErrorClear, mode }) {
  const isEdit = !!initial?.id;
  const a = accent(mode);

  // Resolve options for this dialog open: explicit (preset prefill) wins,
  // otherwise look up by source for an existing card.
  const options = useMemo(() => {
    if (initial?.options?.length) return initial.options;
    if (initial?.source && PRESET_BY_SOURCE[initial.source]) {
      return PRESET_BY_SOURCE[initial.source].cardOptions;
    }
    return null;
  }, [initial]);

  const source = initial?.source || "";
  const defaultMinutes = initial?.defaultMinutes ?? 30;

  const [name, setName] = useState("");
  const [minutes, setMinutes] = useState(defaultMinutes);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNotes(initial?.notes || "");
    if (isEdit) {
      setName(initial.name || (options ? options[0] : ""));
      setMinutes(minutesUntil(initial.target_at));
    } else {
      setName(initial?.name || (options ? options[0] : ""));
      setMinutes(defaultMinutes);
    }
  }, [open, initial, isEdit, options, defaultMinutes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    const mins = Math.max(1, parseInt(minutes, 10) || defaultMinutes);
    setSubmitting(true);
    try {
      const target = new Date(Date.now() + mins * 60 * 1000).toISOString();
      await onSubmit({
        name: name.trim(),
        source: source.trim(),
        notes: notes.trim(),
        target_at: target,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenChange = (v) => {
    if (!v) onErrorClear?.();
    onOpenChange(v);
  };

  const handleClaimAndStart = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const target = new Date(Date.now() + defaultMinutes * 60 * 1000).toISOString();
      const card = await onSubmit({
        name: name.trim(),
        source: source.trim(),
        notes: notes.trim(),
        target_at: target,
      });
      if (card && onClaim) {
        const boardKey = boardKeyFromName(card.name);
        await onClaim(card, boardKey);
      }
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SimpleDialog open={open} onOpenChange={handleOpenChange} className="max-w-md">
      <div
        className={`bg-[#0B0E14]/95 backdrop-blur-xl border ${a.border} rounded-none text-white p-4`}
        data-testid="card-dialog"
      >
        <div className="mb-3">
          <div className={`text-[11px] tracking-[0.3em] uppercase ${a.label} mb-1`}>
            {isEdit ? "Modify Entry" : source || "New Entry"}
          </div>
          <div className="font-display text-2xl tracking-tight">
            {isEdit ? "Edit Card Timer" : `Track ${source || "Card"}`}
          </div>
          <div className="text-slate-400 text-sm mt-1">
            Pick the card and how many minutes remain until it's available again.
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 mt-1">
          <div className="space-y-1.5 flex flex-col items-center text-center">
            <Label className="text-[11px] tracking-[0.3em] uppercase text-slate-400">Card</Label>
            {options ? (
              <select
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`w-full max-w-xs rounded-none bg-[#121822] border border-slate-800 ${a.focusBorder} focus:ring-1 ${a.focusRing.replace("focus-visible:", "focus:")} text-white h-10 px-3 py-2`}
                data-testid="card-name-select"
              >
                {options.map((opt) => (
                  <option
                    key={opt}
                    value={opt}
                    data-testid={`card-option-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`rounded-none bg-[#121822] border-slate-800 ${a.focusBorder} focus-visible:ring-1 ${a.focusRing} text-white`}
                required
                data-testid="card-name-input"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] tracking-[0.3em] uppercase text-slate-400">
              Minutes Remaining
            </Label>
            <div className="flex items-stretch gap-2">
              <Input
                type="number"
                min="1"
                max="100000"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value)}
                className={`rounded-none bg-[#121822] border-slate-800 ${a.focusBorder} focus-visible:ring-1 ${a.focusRing} text-white tabular-nums font-mono w-32`}
                required
                data-testid="card-minutes-input"
              />
              <div className="flex items-center px-3 border border-slate-800 bg-[#121822]/60 text-[11px] tracking-[0.3em] uppercase text-slate-500">
                Minutes
              </div>
              <div className="flex gap-1 ml-auto">
                {[15, 30, 60].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMinutes(m)}
                    data-testid={`quick-minutes-${m}`}
                    className="px-2.5 border border-emerald-400/60 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500 hover:text-black transition-colors text-[11px] tracking-widest uppercase"
                  >
                    {m}m
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[11px] tracking-[0.3em] uppercase text-slate-400">
              Notes (optional)
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Location, tip, keycode, etc."
              className={`rounded-none bg-[#121822] border-slate-800 ${a.focusBorder} focus-visible:ring-1 ${a.focusRing} text-white min-h-[70px]`}
              data-testid="card-notes-input"
            />
          </div>

          {error && (
            <div className="px-3 py-2 border border-orange-500/50 bg-orange-500/10 text-orange-300 text-sm rounded-none mb-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              className="px-4 py-2 border border-slate-700 text-slate-300 hover:border-slate-500 text-xs tracking-widest uppercase"
              data-testid="card-dialog-cancel"
            >
              Cancel
            </button>
            {!isEdit && onClaim && (
              <button
                type="button"
                onClick={handleClaimAndStart}
                disabled={submitting || !name}
                className="px-5 py-2 border border-emerald-500 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500 hover:text-black transition-colors text-xs tracking-widest uppercase disabled:opacity-40"
                data-testid="card-dialog-claim-and-start"
              >
                {submitting ? "Saving…" : "Claim & Track"}
              </button>
            )}
            <button
              type="submit"
              disabled={submitting || !name}
              className={`px-5 py-2 border ${a.submitBtn} transition-colors text-xs tracking-widest uppercase disabled:opacity-40`}
              data-testid="card-dialog-submit"
            >
              {submitting ? "Saving…" : isEdit ? "Save Changes" : "Track Card"}
            </button>
          </div>
        </form>
      </div>
    </SimpleDialog>
  );
}

